import { Alignment, LeftOrRightAlignment, Logger } from "@mcleod/core";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { Table } from "./Table";
import { TableCell } from "./TableCell";
import { TableColumn } from "./TableColumn";
import { TableRow } from "./TableRow";

export abstract class AbstractGeneratedColumnHandler {
    protected column: TableColumn;
    protected log: Logger;
    protected table: Table;

    protected abstract get columnAlignment(): LeftOrRightAlignment;
    protected abstract createColumn(table: Table): TableColumn;
    protected abstract getLogPath(): string;
    protected abstract createPlaceholderCell(tableRow: TableRow): TableCell;

    constructor(table: Table) {
        this.log = DesignableObjectLogManager.getLogger(this.getLogPath());
        this.table = table;
        this.column = this.createColumn(this.table);
    }

    protected addTableListeners(): void {
    }

    syncColumnPlacement() {
        if (!this.table.columns.includes(this.column)) {
            this.addColumnToTable();
        } else {
            this.moveColumnIfNecessary();
        }
    }

    private addColumnToTable() {
        this.table._addGeneratedColumn(this.column);
        this.addTableListeners();
        const targetColumnIndex = this.getTargetIndex();
        const populatedRows: TableRow[] = this.table.allRows?.filter(row => row.populatedDOM === true) ?? [];

        // Add an empty/placeholder cell to any populated rows.  That allows for the column to be moved before the async
        // action of creating the new/real cells starts.  Then we can replace the empty cell in each row with the
        // new/real cell.  This helps avoid a situation where other columns are added to the table before the column
        // move finishes.
        // Note that it's also possible that the table will be cleared, and it's rows redisplayed, before the
        // placeholder cells are replaced.  It's ok if that happens; in that case the moved column will regenerate its
        // cells.
        const rowsAndPlaceholderCells = populatedRows.map(tableRow => {
            const cell = this.createPlaceholderCell(tableRow);
            tableRow.addCell(cell);
            return { tableRow, cell };
        });
        this.table._moveGeneratedColumn(this.column.index, targetColumnIndex);
        rowsAndPlaceholderCells.forEach(async (rowsAndPlaceholderCell) => {
            const tableRow = rowsAndPlaceholderCell.tableRow;
            const placeholderCell = rowsAndPlaceholderCell.cell;
            const realCell = await tableRow.createCell(this.column);
            tableRow._replaceCell(placeholderCell, realCell);
        });
    }

    private moveColumnIfNecessary() {
        const targetColumnIndex = this.getTargetIndex();
        this.table._moveGeneratedColumn(this.column.index, targetColumnIndex);
    }

    /**
     * Determines the index at which the generated column should reside.  In a normal table:
     * - Expand Cells are always rendered as the outermost cell in the row (displays first or last).
     * - Select Checkbox Cells are rendered as the second-outermost cell (adjacent to the expand cell)
     * - Tools Cells are rendered as the innermost generated cell (adjacent to normal table cells in the row)
     *
     * The one exception is, when in the UI Designer/Tailor, the Designer Tools Cells are always rendered as the
     * right-most cell in a row.
     *
     * Note that the method assumes that, when called as part of adding a column, that the column will have already
     * been added to table's columns array before this method is called.
     */
    private getTargetIndex(): number {
        const designerToolsColumn = this.table.designerToolsColumn;
        const expandColumn = this.table.expandColumn;
        const selectCheckboxColumn = this.table.selectCheckboxColumn;
        const toolsColumn = this.table.toolsColumn;
        const normalColumns = this.table.normalColumns;

        const sortedColumns: TableColumn[] = [];

        const pushDesignerToolsColumn = () => {
            if (designerToolsColumn) {
                sortedColumns.push(designerToolsColumn);
            } else if (this.table.isDesignerToolsColumn(this.column)) {
                sortedColumns.push(this.column);
            }
        };

        const pushExpandedColumn = () => {
            if (expandColumn) {
                sortedColumns.push(expandColumn);
            } else if (this.table.isExpandColumn(this.column)) {
                sortedColumns.push(this.column);
            }
        };

        const pushSelectCheckboxColumn = () => {
            if (selectCheckboxColumn) {
                sortedColumns.push(selectCheckboxColumn);
            } else if (this.table.isSelectCheckboxColumn(this.column)) {
                sortedColumns.push(this.column);
            }
        };

        const pushToolsColumn = () => {
            if (toolsColumn) {
                sortedColumns.push(toolsColumn);
            } else if (this.table.isToolsColumn(this.column)) {
                sortedColumns.push(this.column);
            }
        };

        if (this.table.expandCellAlignment === Alignment.LEFT) {
            pushExpandedColumn();
        }
        if (this.table.selectCheckboxAlignment === Alignment.LEFT) {
            pushSelectCheckboxColumn();
        }
        if (this.table.toolsCellAlignment === Alignment.LEFT) {
            pushToolsColumn();
        }

        sortedColumns.push(...normalColumns);

        if (this.table.toolsCellAlignment === Alignment.RIGHT) {
            pushToolsColumn();
        }
        if (this.table.selectCheckboxAlignment === Alignment.RIGHT) {
            pushSelectCheckboxColumn();
        }
        if (this.table.expandCellAlignment === Alignment.RIGHT) {
            pushExpandedColumn();
        }

        if (this.table._designer != null) {
            pushDesignerToolsColumn();
        }
        
        return sortedColumns.indexOf(this.column);
    }

    removeColumn() {
        this.table._removeGeneratedColumn(this.column);
    }
}
